import React from 'react';
import { __, sprintf } from '@wordpress/i18n';
import { useTimezone } from '../contexts/TimezoneContext';
import './TimezoneIndicator.css';

interface TimezoneIndicatorProps {
	className?: string;
	showIndicator?: boolean;
	customMessage?: string | null;
}

/**
 * Get timezone abbreviation (e.g., PST, EST, CDT) for a given timezone
 * Uses the visitor's locale for proper localization
 */
function getTimezoneAbbreviation(timezone: string): string {
	try {
		// Use the visitor's locale (or default to 'en-US' if not available)
		const locale = navigator.language || 'en-US';

		// Create a date formatter for the timezone
		const formatter = new Intl.DateTimeFormat(locale, {
			timeZone: timezone,
			timeZoneName: 'short',
		});

		// Format a date and extract the timezone abbreviation
		const parts = formatter.formatToParts(new Date());

		const timeZonePart = parts.find((part) => part.type === 'timeZoneName');

		return timeZonePart?.value || timezone;
	} catch (e) {
		return timezone;
	}
}

export const TimezoneIndicator: React.FC<TimezoneIndicatorProps> = ({
	className = '',
	showIndicator = true,
	customMessage = null,
}) => {
	const {
		mode,
		siteTimezone,
		localTimezone,
		siteTimezoneLabel,
		localTimezoneLabel,
		siteTimezoneOffset,
		localTimezoneOffset,
	} = useTimezone();

	// Don't show indicator if disabled
	if (!showIndicator) {
		return null;
	}

	// Don't show indicator if timezones have the same offset
	// This prevents showing the indicator when timezones are functionally equivalent
	// even if they have different names (e.g., America/Chicago and America/Winnipeg)
	if (
		siteTimezone === localTimezone ||
		siteTimezoneLabel === localTimezoneLabel ||
		siteTimezoneOffset === localTimezoneOffset
	) {
		return null;
	}

	const currentTimezone = mode === 'site' ? siteTimezone : localTimezone;
	const timezoneAbbr = getTimezoneAbbreviation(currentTimezone);

	// Use custom message if provided, otherwise use default
	const message = customMessage
		? customMessage.replace('{timezone}', timezoneAbbr)
		: // translators: %s is the timezone abbreviation, e.g., PST, EST, CDT
			sprintf(__('All times shown in %s', 'gp-bookings'), timezoneAbbr);

	return (
		<div className={`gpb-timezone-indicator ${className}`}>
			<small>{message}</small>
		</div>
	);
};
